import { SecurityOutlined as SecurityIcon } from '@mui/icons-material'
import { ClassNameMap, Theme } from '@mui/material'
import { FilterObject } from 'genesis-suite/types/utilTypes'
import { isEmpty } from 'lodash'
import userRoles from '../../../lib/userRoles'
import { ColDef } from '@ag-grid-community/core'
import CompanySelector from '../CompanySelector'
import DomainSelector from '../DomainSelector'

const EMAIL_REGEX = /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/
const PHONE_REGEX = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/

function validateEmail(value: any) {
    return EMAIL_REGEX.test(value)
}

function validatePhone(value: any) {
    return PHONE_REGEX.test(value) || value === '' || value === '--'
}

export function buildXGridColumns(
    toggleUser: (user: any) => void,
    selectedUser: { userId: any },
    classes: ClassNameMap<'table' | 'disabled' | 'actionButton' | 'submitSpinner' | 'showPointer'>,
    theme: Theme,
    context: any
) {
    const columns: ColDef[] = [
        {
            pinned: 'left',
            colId: 'checkbox',
            lockPosition: 'left',
            filter: false,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            resizable: false,
            width: 20,
            minWidth: 50,
            suppressHeaderMenuButton: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
        },
        { headerName: 'ID', field: 'id', width: 90, hide: true },
        { headerName: 'Username', field: 'userId', width: 150, editable: false },
        { headerName: 'Display Name', field: 'screenAlias', width: 150, editable: false, filter: false },
        { headerName: 'First', field: 'firstName', width: 150, editable: false },
        { headerName: 'Last', field: 'lastName', width: 150, editable: false },
        {
            headerName: 'Email',
            field: 'email',
            width: 150,
            editable: false,
            valueSetter: (params: any) => {
                // Validate the new value
                const isValid = validateEmail(params.newValue)
                // Update the row data with the validation status
                if (params.newValue !== '') {
                    params.data.emailValidation = { isValid }
                } else {
                    params.data.emailValidation = undefined
                }
                params.data[params.colDef.field] = params.newValue
                return true
            },
            cellClassRules: {
                'invalid-cell': (params: any) => {
                    return (
                        params.data.emailValidation &&
                        typeof params.data.emailValidation.isValid === 'boolean' &&
                        !params.data.emailValidation.isValid
                    )
                },
            },
            tooltipValueGetter: (params: any) =>
                params.data.emailValidation && !params.data.emailValidation.isValid
                    ? 'Please enter a valid email address'
                    : '',
        },
        {
            headerName: 'Phone',
            field: 'phone',
            width: 150,
            editable: false,
            cellEditor: 'agNumberCellEditor',
            // cellStyle: (params: any) => (validatePhone(params.newValue) === false ? { backgroundColor: 'red' } : {}),
            // cellEditorParams: {
            //     useFormatter: true,
            //     validator: validatePhone,
            //     errorMessage: 'Please enter a valid phone number',
            // },
            valueSetter: (params: any) => {
                console.log(params)
                // Validate the new value
                const isValid = validatePhone(params.newValue)
                // Update the row data with the validation status
                if (params.newValue !== '') {
                    params.data.validatePhone = { isValid }
                } else {
                    params.data.validatePhone = undefined
                }
                params.data[params.colDef.field] = params.newValue
                return true
            },
            cellClassRules: {
                'invalid-cell': (params: any) => {
                    return (
                        params.data.validatePhone &&
                        typeof params.data.validatePhone.isValid === 'boolean' &&
                        !params.data.validatePhone.isValid
                    )
                },
            },
            tooltipValueGetter: (params: any) =>
                params.data.validatePhone && !params.data.validatePhone.isValid
                    ? 'Please enter a valid phone number'
                    : '',
        },
        {
            headerName: 'External Login',
            field: 'isExternal',
            width: 150,
            editable: false,
            valueFormatter: (params: any) => (params.value ? 'Yes' : 'No'),
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['Yes', 'No'],
            },
        },
        {
            headerName: 'Role',
            field: 'role',
            width: 150,
            editable: (content: any) => content !== 'Owner',
            cellStyle: (params: any) =>
                params.value === 'Owner' ? { backgroundColor: '#e0e0e0', opacity: '.65', cursor: 'not-allowed' } : {},
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: ['Admin', 'Super User', 'User'] },
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['User', 'Super User', 'Admin'],
            },
        },
        {
            headerName: 'Filters',
            field: 'edit',
            width: 60,
            sortable: false,
            filter: false,
            cellRenderer: (params: any) => {
                const onClick = (e: any) => {
                    if (params.data.role === 'Owner' || params.data.role === 'Admin') return null
                    return toggleUser(params.data)
                }
                const isSelectedUser = selectedUser && selectedUser.userId === params.data.userId
                return (
                    <SecurityIcon
                        onClick={onClick}
                        fontSize="small"
                        style={{ color: theme.palette.primary.main }}
                        className={classes.showPointer}
                        htmlColor={isSelectedUser ? theme.palette.primary.main : '#000'}
                    />
                )
            },
        },
        {
            headerName: 'Can Publish Scenario',
            field: `scenarioPublishPermissions.${context}`,
            width: 150,
            editable: true,
            valueFormatter: (params: any) => (params.value ? 'True' : 'False'),
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: [true, false] },
            filter: 'agSetColumnFilter',
            filterParams: { values: [true, false] },
        },
        {
            headerName: 'Can Approve Scenario',
            field: `scenarioApprovalPermissions.${context}`,
            width: 150,
            editable: true,
            valueFormatter: (params: any) => (params.value ? 'True' : 'False'),
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: [true, false] },
            filter: 'agSetColumnFilter',
            filterParams: { values: [true, false] },
        },
        {
            headerName: 'Company',
            field: `company`,
            width: 150,
            editable: params => {
                return !params.data.companyId
            },
            cellEditor: CompanySelector,
            cellEditorParams: params => {
                return {
                    params,
                }
            },
            filter: false,
        },
        {
            headerName: 'Domain',
            field: `domain`,
            width: 150,
            editable: params => {
                return !params.data.domainId
            },
            cellEditor: DomainSelector,
            cellEditorParams: params => {
                return {
                    params,
                }
            },
            filter: false,
        },
        { headerName: 'Created By', field: 'createdBy', width: 150, editable: false },
        { headerName: 'Modified By', field: 'modifiedBy', width: 150, editable: false },
    ]
    // const colDefs = columns?.map(column => ({
    //     ...column,
    // }))
    return columns
}

export function getFilterCondition(filterType: string) {
    switch (filterType) {
        case 'notContains':
            return 'not contains'
        case 'contains':
            return 'contains'
        case 'equals':
            return '='
        case 'notEqual':
            return '!='
        case 'startsWith':
            return 'starts with'
        case 'endsWith':
            return 'ends with'
        default:
            break
    }
}

export function getFilters(filterModel) {
    const filters: FilterObject[] = []
    if (!isEmpty(filterModel)) {
        Object.keys(filterModel).forEach(key => {
            const { filter, type, values } = filterModel[key]
            if (key === 'role') {
                const roles = ['User', 'Super User', 'Admin']
                const difference = roles.filter(x => !values.includes(x))
                if (!isEmpty(difference)) {
                    difference.forEach(role => {
                        filters.push({
                            Name: 'permissionId',
                            value: Object.keys(userRoles).find(
                                key => userRoles[key].toLowerCase() === role.toLowerCase()
                            ),
                            condition: '!=',
                        })
                    })
                }
            } else if (key === 'isExternal' && values.length === 1) {
                filters.push({
                    Name: 'is_external',
                    value: values[0] === 'Yes' ? 'true' : 'false',
                    condition: '=',
                })
            } else {
                const filterObject: FilterObject = {
                    Name: key,
                    value: filter,
                    condition: getFilterCondition(type),
                }
                filters.push(filterObject)
            }
        })
    }

    return filters
}
